import {
  buildCoachAvailabilityQuery,
  MAX_SCHEDULING_WINDOW_DAYS,
} from '@efdigital/ll-addon-coaching/utils/third-party/cronofy/cronofy-availability-query';
import type { AvailabilityQuery, QueryPeriod, WeekDay } from 'cronofy-elements';

/**
 * Availability preview query periods.
 *
 * Week boundaries use the **browser's local timezone**, matching the Cronofy
 * Availability Viewer when no `tzid` is set (the preview does not pass one).
 * A coach in Tokyo and a coach in London each see weeks aligned to their own
 * local calendar — which is what we want for a self-service sync check.
 *
 * The scheduling horizon still adds fixed 24-hour UTC milliseconds (same as
 * {@link buildDefaultQueryPeriods} in the addon). That only picks which week
 * to extend through; week padding uses local calendar days.
 */

/** Match the default coaching config scheduling window shown in this preview. */
export const AVAILABILITY_PREVIEW_SCHEDULING_WINDOW_DAYS = 14;

/** Match {@link availability.gts} working-hours and viewer grid configuration. */
export const AVAILABILITY_PREVIEW_WEEK_START: WeekDay = 'monday';

const WEEK_START_DAY_INDEX: Record<WeekDay, number> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

function toCronofyTime(date: Date): string {
  return `${date.toISOString().slice(0, 19)}Z`;
}

function startOfWeek(date: Date, weekStartDay: WeekDay): Date {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);

  const weekStartIndex = WEEK_START_DAY_INDEX[weekStartDay];
  const daysSinceStart = (start.getDay() - weekStartIndex + 7) % 7;
  start.setDate(start.getDate() - daysSinceStart);

  return start;
}

function endOfWeekExclusive(date: Date, weekStartDay: WeekDay): Date {
  const end = startOfWeek(date, weekStartDay);
  end.setDate(end.getDate() + 7);
  return end;
}

/** Round {@link date} up to the next whole minute. */
function roundUpToNextMinute(date: Date): Date {
  const rounded = new Date(date);
  rounded.setSeconds(0, 0);
  rounded.setMinutes(rounded.getMinutes() + 1);
  return rounded;
}

/**
 * Pad the end of the bookable window to a full week so the Availability Viewer
 * does not show blank trailing columns when confirming calendar sync.
 *
 * The start is **not** padded back to the start of the week: Cronofy rejects a
 * query period that starts in the past with a 422 `errors.min_date_exceeded`,
 * which fails the whole current week ("There was an error loading the
 * element"). Days already gone simply render as unavailable.
 */
export function buildFullWeekPreviewQueryPeriods(
  schedulingWindowDays: number,
  weekStartDay: WeekDay,
  now = new Date()
): QueryPeriod[] {
  const schedulingHorizon = new Date(
    now.getTime() +
      Math.min(schedulingWindowDays, MAX_SCHEDULING_WINDOW_DAYS) *
        24 *
        60 *
        60 *
        1000
  );
  schedulingHorizon.setUTCSeconds(0, 0);

  const start = roundUpToNextMinute(now);
  const end = endOfWeekExclusive(schedulingHorizon, weekStartDay);

  if (end <= start) {
    return [];
  }

  return [{ start: toCronofyTime(start), end: toCronofyTime(end) }];
}

/** Availability query for the coach settings preview screen. */
export function buildAvailabilityPreviewQuery({
  coachSub,
}: {
  coachSub: string;
}): AvailabilityQuery {
  const query = buildCoachAvailabilityQuery({
    durationMinutes: 30,
    schedulingWindowDays: AVAILABILITY_PREVIEW_SCHEDULING_WINDOW_DAYS,
    coachSub,
  });

  return {
    ...query,
    query_periods: buildFullWeekPreviewQueryPeriods(
      AVAILABILITY_PREVIEW_SCHEDULING_WINDOW_DAYS,
      AVAILABILITY_PREVIEW_WEEK_START
    ),
  };
}
